import { Checkbox, Grid, useBoolean } from '@chakra-ui/react';
import { Decimal } from 'decimal.js';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { ListUtils } from '../../../../../framework/utils/ListUtils';
import { TypeUtils } from '../../../../../framework/utils/UtilityTypes';
import { useGlobalStateMachine } from '../../../../GlobalStore';
import { useFormItems } from '../../../../hooks/formItems';
import { Formatter } from '../../../../utils/FormatUtils';
import { useMasterContext } from '../../../MasterProvider';
import { AssignWorkInfoDaySearchResultForm } from '../WorkContent';
import { AssignWorkInfoProps } from './AssignWorkInfo';
import { GridItem } from './GridItem';

type DailyWorkProps = AssignWorkInfoProps & {
  isOvertime: boolean;
  /**
   * 現場数
   */
  matterSize: number;
  totalWorkTime: number;
  workTimeFrom: string | null;
  workTimeTo: string | null;
};

export const useDailyWork = () => {
  const { state } = useGlobalStateMachine();
  const [isAbsence, { toggle }] = useBoolean();

  const master = useMasterContext();
  const ATTENDANCE_TYPE_PRESENT = React.useMemo<number>(
    () => state.appConfig.facilitySettings?.ATTENDANCE_TYPE_ID_WORK as number,
    [state.appConfig.facilitySettings]
  );
  const form = useFormContext<AssignWorkInfoDaySearchResultForm>();
  const { FormMultiSelect, FormText } = useFormItems(form);

  /**
   * 日次就業エリア
   *
   * @returns
   */
  const DailyWork: React.FunctionComponent<DailyWorkProps> = ({
    isOvertime,
    matterSize,
    mode,
    totalWorkTime,
    workTimeFrom,
    workTimeTo,
  }) => {
    const hour22 = React.useMemo(() => {
      const h22 = Formatter.createDate(workTimeFrom!);
      h22.setHours(22);
      h22.setMinutes(0);
      h22.setSeconds(0);
      h22.setMilliseconds(0);

      return h22;
    }, [workTimeFrom]);
    /**
     * 深夜残業時間 (SE のみ表示する)
     *
     * 8時間を超えた労働時間と22時以降の労働時間のうち，小さい方<br />
     * 休憩時間は22時以前に寄せるためここでは考慮しない
     */
    const midnightOvertimeWork = React.useMemo(
      () =>
        state.userInfo?.roleOptions.isOutsourcer
          ? 0 // 外注で毎度計算させるのはもったいないので固定値
          : Math.min(
              Math.max(0, totalWorkTime - 480), // 8時間を超えた労働時間
              TypeUtils.isNil(workTimeTo)
                ? 0 // 終了時間未設定
                : Formatter.createDate(workTimeTo).valueOf() <= hour22.valueOf()
                ? 0 // 22時以前に終了
                : new Decimal(
                    Formatter.createDate(workTimeTo).valueOf() -
                      hour22.valueOf()
                  )
                    .dividedToIntegerBy(60000)
                    .toNumber()
            ),
      [form, hour22, matterSize]
    );

    return (
      <Grid
        backgroundColor="white"
        borderColor="gray.300"
        borderRadius="md"
        borderWidth={1}
        padding={2}
        templateColumns="1fr 1fr"
        width="100%"
      >
        <GridItem colSpan={2} hidden textAlign="left">
          <Checkbox
            isChecked={isAbsence}
            isDisabled={mode === 'view'}
            onChange={toggle}
          >
            欠勤
          </Checkbox>
        </GridItem>
        <GridItem>
          <FormText
            label="開始時刻"
            name="reference.dailyWork"
            value={() => Formatter.dateTimeOfWeekString(workTimeFrom) || '―'}
          />
        </GridItem>
        <GridItem>
          <FormText
            label="終了時刻"
            name="reference.dailyWork"
            value={() => Formatter.dateTimeOfWeekString(workTimeTo) || '―'}
          />
        </GridItem>
        <GridItem>
          <FormText
            label="休憩時間"
            name="reference.dailyWork"
            value={() => {
              const effectiveAttendanceTypeIndexes = form
                .getValues(
                  Array(matterSize)
                    .fill(null)
                    .map(
                      (_, index) =>
                        `results.${index}.assignedWorkerWorkInfo.attendanceTypeId` as any
                    )
                )
                .map((id, index) =>
                  id === ATTENDANCE_TYPE_PRESENT ? index : null
                )
                .filter((index) => index !== null);

              return Formatter.workingTime(
                form
                  .getValues(
                    Array(matterSize)
                      .fill(null)
                      .map(
                        (_, index) =>
                          `results.${index}.assignedWorkerWorkInfo.restTime` as any
                      )
                  )
                  .filter(
                    (value, index) =>
                      effectiveAttendanceTypeIndexes.includes(index) &&
                      !TypeUtils.isNil(value)
                  )
                  .map((value) => parseInt(value.toString()))
                  .filter((value) => !TypeUtils.isNilNumber(value))
                  .reduce((prev: number, cur: number) => prev + cur, 0)
              );
            }}
          />
        </GridItem>
        <GridItem>
          <FormText
            label="総労働時間"
            name="reference.dailyWork"
            value={() => Formatter.workingTime(totalWorkTime)}
          />
        </GridItem>

        {/* 以降は SE のみ */}
        <GridItem hidden={state.userInfo?.roleOptions?.isOutsourcer}>
          <FormText
            label="残業時間"
            name="reference.dailyWork"
            value={() =>
              Formatter.workingTime(
                Math.max(0, totalWorkTime - midnightOvertimeWork - 480)
              )
            }
          />
        </GridItem>
        <GridItem hidden={state.userInfo?.roleOptions?.isOutsourcer}>
          <FormText
            label="深夜残業時間"
            name="reference.dailyWork"
            value={() => Formatter.workingTime(midnightOvertimeWork)}
          />
        </GridItem>
        <GridItem
          colSpan={2}
          hidden={state.userInfo?.roleOptions?.isOutsourcer}
        >
          <FormMultiSelect<number>
            label="ポイント"
            mode={mode}
            name="extraFees"
            options={Object.values(master.extraFee)
              .sort(ListUtils.sort((m) => m.displayOrder))
              .map((extraFee) => ({
                value: extraFee.id,
                valueToDisplay: extraFee.name,
              }))}
          />
        </GridItem>
      </Grid>
    );
  };

  return { DailyWork, isAbsence, toggle };
};
